import { ValueGetterFunc } from "@ag-grid-community/core";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import Decimal from "decimal.js";
import {
  getCreditFacilityInterestExpenseAdjustmentsTableRetrieveQueryKey,
  getCreditFacilityInterestExpenseEventsRetrieveQueryKey,
  getCreditFacilityInterestExpenseRetrieveQueryKey,
  useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense,
  useCreditFacilityInterestExpensesAdjust,
} from "gen/endpoints/credit-facility/credit-facility";
import { useCreditFacilityLoanListSuspense } from "gen/endpoints/credit-facility-loan/credit-facility-loan";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { CreditFacilityExpenseAdjustmentsUpdateDataRequest } from "gen/model/creditFacilityExpenseAdjustmentsUpdateDataRequest";
import { DayCountEnum } from "gen/model/dayCountEnum";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import { PercentTableCellValue } from "gen/model/percentTableCellValue";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { get } from "lodash-es";
import { useCallback } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { DataTableHeaderOrCellClass } from "shared/components/table/data/DataTableTypes";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import {
  formatIsoDateToDisplay,
  getAnnualizedRateFromDailyRate,
  getDailyRateFromAnnualizedRate,
} from "shared/utils/DateUtils";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import invariant from "shared/utils/TinyInvariant";
import { useDeepCompareEffect } from "use-deep-compare";

const headerOrCellClass: DataTableHeaderOrCellClass = (props) =>
  clsx({
    "or-column": props.column?.getColId() == "or",
  });

// FIXME: once we decouple DataTable from Form, we can remove this but for now we need to manually watch the form and refresh cells on input changes
const RefreshCellsOnInputChange: React.FC<
  React.PropsWithChildren<{
    form: UseFormReturn<CreditFacilityExpenseAdjustmentsUpdateDataRequest>;
  }>
> = ({ form, children }) => {
  const values = useWatch({
    control: form.control,
  });
  const { agGrid } = useMaybernTableUIContext();
  const agGridApi = tryGetAgGridApi(agGrid);
  useDeepCompareEffect(() => {
    if (agGridApi) {
      agGridApi.refreshCells();
    }
  }, [values]);
  return children;
};

/* eslint-disable @typescript-eslint/no-unnecessary-condition */
const disableDataTableField = ({
  columnData,
  colId,
}: {
  columnData: GenericTableRow;
  colId: string;
}): GenericTableCell => ({
  ...columnData.cells[colId],
  form_field: {
    name: "",
    ...columnData.cells[colId]?.form_field,
    is_editable: false,
  },
});

const getUpdatedOutstandingAmountWhenRateChanges = (
  columnData: GenericTableRow,
  formValues: CreditFacilityExpenseAdjustmentsUpdateDataRequest,
  rateDayCount: DayCountEnum,
): Decimal => {
  const effectiveDate = new Date(
    (columnData?.cells["effective_date"]?.value as TextTableCellValue)?.text ||
      "",
  );
  const principalOutstanding = decimalOrZero(
    (
      columnData.cells["principal_outstanding"]?.value as
        | MoneyTableCellValue
        | undefined
    )?.amount,
  );
  const adjustedRate = decimalOrZero(
    get(
      formValues,
      columnData.cells["interest_rate_adjustment"]?.form_field?.name as string,
    ),
  );
  const interestExpense = principalOutstanding.mul(
    getDailyRateFromAnnualizedRate(effectiveDate, adjustedRate, rateDayCount) ??
      1,
  );
  return interestExpense;
};

const getUpdatedInterestRateWhenOutstandingChanges = (
  columnData: GenericTableRow,
  formValues: CreditFacilityExpenseAdjustmentsUpdateDataRequest,
  rateDayCount: DayCountEnum,
): Decimal | undefined => {
  const effectiveDate = new Date(
    (columnData?.cells["effective_date"]?.value as TextTableCellValue)?.text ||
      "",
  );
  const principalOutstanding = decimalOrZero(
    (
      columnData.cells["principal_outstanding"]?.value as
        | MoneyTableCellValue
        | undefined
    )?.amount,
  );
  const adjustedOutstanding = decimalOrZero(
    get(
      formValues,
      columnData.cells["interest_expense_amount_adjustment"]?.form_field
        ?.name as string,
    ),
  );
  const updatedInterestRate = getAnnualizedRateFromDailyRate(
    effectiveDate,
    adjustedOutstanding.dividedBy(principalOutstanding),
    rateDayCount,
  );
  return updatedInterestRate?.mul(100);
};
/* eslint-enable @typescript-eslint/no-unnecessary-condition */

export const CreditFacilityExpensesAdjustmentPage: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const { creditFacilityId, fundFamilyId, fundFamilyEventId, loanId } =
    useRequiredStringParams();

  const { data: loans } = useCreditFacilityLoanListSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const currentLoan = loans.find((loan) => loan.id === loanId);
  invariant(currentLoan);

  const { data: adjustmentsData } =
    useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEventId,
      loanId,
    );
  const { mutateAsync: bulkUpdateExpenses } =
    useCreditFacilityInterestExpensesAdjust();

  const onSave = useCallback(
    async (data: CreditFacilityExpenseAdjustmentsUpdateDataRequest) => {
      await bulkUpdateExpenses({
        fundFamilyId,
        creditFacilityId,
        fundFamilyEventId,
        loanId,
        data: {
          expenses: Object.entries(data.expenses).map(([date, data]) => ({
            ...data,
            effective_date: date,
            interest_rate_adjustment: data.interest_rate_adjustment || null,
            interest_expense_amount_adjustment:
              data.interest_expense_amount_adjustment || null,
          })),
        },
      });
    },
    [
      fundFamilyId,
      bulkUpdateExpenses,
      loanId,
      creditFacilityId,
      fundFamilyEventId,
    ],
  );

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const backToExpenses = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .creditFacilities({})
        .creditFacility({ creditFacilityId })
        .event({ fundFamilyEventId })
        .expenses({}).$,
    );
  }, [navigate, fundFamilyId, creditFacilityId, fundFamilyEventId]);
  const form = useForm<CreditFacilityExpenseAdjustmentsUpdateDataRequest>({});
  /* eslint-disable @typescript-eslint/no-unnecessary-condition */
  const valueGetter = useCallback<
    ValueGetterFunc<GenericTableRow, GenericTableCell>
  >(
    (params) => {
      const formValues = form.getValues();
      const colId = params.column.getColId();
      const columnData = params.node?.data;

      if (columnData) {
        if (colId === "updated_interest_expense_amount") {
          const updatedExpenseAmount =
            getUpdatedOutstandingAmountWhenRateChanges(
              columnData,
              formValues,
              currentLoan.rate_day_count,
            );
          return {
            value: {
              type: "money",
              amount: updatedExpenseAmount.toString(),
            } as MoneyTableCellValue,
          } as GenericTableCell;
        } else if (colId === "updated_interest_rate") {
          const updatedInterestRate =
            getUpdatedInterestRateWhenOutstandingChanges(
              columnData,
              formValues,
              currentLoan.rate_day_count,
            );
          return {
            value: {
              type: "percent",
              value: updatedInterestRate?.toString() ?? "",
            } as PercentTableCellValue,
          } as GenericTableCell;
        } else if (colId == "interest_rate_adjustment") {
          const adjustedOutstanding = decimalOrZero(
            get(
              formValues,
              columnData.cells["interest_expense_amount_adjustment"]?.form_field
                ?.name as string,
            ),
          );
          if (!adjustedOutstanding.isZero()) {
            return disableDataTableField({
              columnData,
              colId,
            });
          }
        } else if (colId == "interest_expense_amount_adjustment") {
          const adjustedRate = decimalOrZero(
            get(
              formValues,
              columnData.cells["interest_rate_adjustment"]?.form_field
                ?.name as string,
            ),
          );
          if (!adjustedRate.isZero()) {
            return disableDataTableField({
              columnData,
              colId,
            });
          }
        }
      }
    },
    [form, currentLoan.rate_day_count],
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={`Adjust interest expenses: ${currentLoan.name}`}
        documentTitle={
          fundFamilyEvent.state == FundFamilyEventStateMachineFieldsEnum.draft
            ? t("creditFacility:postExpense.workflow")
            : t("creditFacility:postExpense.dailyBreakdown")
        }
      />
      <PageBody>
        <Form
          onSuccess={async () => {
            await invalidateQueryKeys(
              queryClient,
              getCreditFacilityInterestExpenseAdjustmentsTableRetrieveQueryKey(
                fundFamilyId,
                creditFacilityId,
                fundFamilyEventId,
                loanId,
              ),
              getCreditFacilityInterestExpenseEventsRetrieveQueryKey(
                fundFamilyId,
                creditFacilityId,
              ),
              getCreditFacilityInterestExpenseRetrieveQueryKey(
                fundFamilyId,
                creditFacilityId,
                fundFamilyEventId,
              ),
            );
            backToExpenses();
          }}
          onSubmit={onSave}
          form={form}
        >
          <TableBox isFullHeight={true} isFullWidth={true}>
            <TableBoxHeader>
              <TableBoxTitle>
                {`${formatIsoDateToDisplay(
                  adjustmentsData.period_start,
                )} - ${formatIsoDateToDisplay(
                  adjustmentsData.period_end,
                )}: ${t("creditFacility:postExpense.dailyInterestExpense")}`}
              </TableBoxTitle>
              <DataTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <RefreshCellsOnInputChange form={form}>
                <DataTable
                  sx={{
                    ".or-column > *": {
                      justifyContent: "center",
                    },
                  }}
                  headerOrCellClass={headerOrCellClass}
                  data={adjustmentsData.table}
                  shouldUnregisterFormFields={false}
                  valueGetter={valueGetter}
                />
              </RefreshCellsOnInputChange>
              <FormFooter>
                <FormFooterActions>
                  <FormCancelButton onClick={backToExpenses}>
                    {t("common:cancel")}
                  </FormCancelButton>
                  <FormSubmitButton>
                    {t("creditFacility:postExpense.saveAdjustments")}
                  </FormSubmitButton>
                </FormFooterActions>
              </FormFooter>
            </TableBoxBody>
          </TableBox>
        </Form>
      </PageBody>
    </PageContent>
  );
};
